/**
 * frontend/src/lib/messages-auth.ts
 * `#840` — **CE QUE LE SERVEUR NOMME, L'ÉCRAN L'ÉCRIT.**
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠⚠ **CE QUE ÇA CORRIGE, ET QUI SE LISAIT À L'ÉCRAN.**
 * ══════════════════════════════════════════════════════════════════════════════
 *   *Le T0 `#838` §F.2 l'a mesuré : l'authentification rendait **huit messages
 *   anglais et cinq français** — dont trois **sans accents** — et le front les
 *   peignait tels quels. **Un compte verrouillé lisait `Account temporarily
 *   locked. Try again later.` en plein écran français.***
 *
 * ⚠ *Patron `#817` ⑴, appliqué au bon endroit : le serveur ne compose plus
 *   aucune phrase (`auth/handlers.ts`), il rend un CODE. **La langue n'est plus
 *   décidée par celui qui refuse, mais par celui qui lit.***
 *
 * ⚠⚠ **ET LE DOUBLON `#552`/`#553` DISPARAÎT AVEC.** *Le serveur composait la
 *   phrase « session au plafond », `LoginPage` en réécrivait une seconde,
 *   proche mais différente, à partir du `?raison=`. **Deux textes pour un
 *   fait.** Désormais `?raison=` porte **le code**, et les deux chemins —
 *   réponse d'API et redirection — désignent **la même clé**.*
 *
 * ⚠ **UN CODE INCONNU REND LE CODE**, jamais une phrase inventée : c'est la
 *   règle du repli depuis le `#834` (`funnel.ts`) et le `#837` (`etapes.ts`).
 */
import type { CleI18n } from '@i18n';
import type { TraduireI18n } from './mots';

/**
 * Le code du serveur → le mot de l'écran.
 *
 * ⚠ *Les cinq premiers ne sont pas la propriété de l'auth : `sessionGuard` et
 *   `fichiersProteges` les émettent aussi. C'est pourquoi ils gardent leur nom
 *   d'origine plutôt qu'un préfixe `AUTH_` (voir `shared/errors.ts`).*
 */
export const MESSAGE_AUTH: Record<string, CleI18n> = {
  // ── partagés avec les middlewares ──
  ACCOUNT_LOCKED:       'commun.srv.ACCOUNT_LOCKED',
  ACCOUNT_INACTIVE:     'commun.srv.ACCOUNT_INACTIVE',
  SESSION_EXPIRED:      'commun.srv.SESSION_EXPIRED',
  SESSION_MAX_LIFETIME: 'auth.sessionDureeMax',
  REFRESH_TOKEN_REUSED: 'auth.sessionJetonRejoue',
  // ── propres à l'authentification ──
  AUTH_INVALID_BODY:        'commun.srv.VALIDATION_ERROR',
  AUTH_INVALID_CREDENTIALS: 'commun.srv.AUTH_INVALID_CREDENTIALS',
  AUTH_SESSION_INVALID:     'commun.srv.AUTH_SESSION_INVALID',
  AUTH_TOTP_INVALID:        'commun.srv.AUTH_TOTP_INVALID',
  AUTH_TOTP_REQUIRED:       'commun.srv.AUTH_TOTP_REQUIRED',
  AUTH_2FA_ALREADY:         'commun.srv.AUTH_2FA_ALREADY',
  AUTH_CODE_6_DIGITS:       'commun.srv.AUTH_CODE_6_DIGITS',
  AUTH_2FA_PREPARE_FIRST:   'commun.srv.AUTH_2FA_PREPARE_FIRST',
  AUTH_CODE_INVALID:        'commun.srv.AUTH_CODE_INVALID',
};

/**
 * Le mot de l'écran pour un refus du serveur.
 *
 * @param code   le code rendu par l'API (ou porté par `?raison=`)
 * @param t      le traducteur, passé en paramètre (patron `RowTr`, `#810`)
 * @param repli  ce qu'on rend si le code est inconnu — par défaut, le code
 */
export function libelleAuth(
  code: string | null | undefined,
  t: TraduireI18n,
  repli?: string,
): string {
  if (!code) return repli ?? '';
  const cle = MESSAGE_AUTH[code];
  return cle ? t(cle) : (repli ?? code);
}

/**
 * `#840` ★1 — **LES TROIS RAISONS DE DÉCONNEXION, PORTÉES PAR LEUR CODE.**
 *
 * ⚠ *`?raison=plafond` disait un mot français dans une URL ; il dit maintenant
 *   `?raison=SESSION_MAX_LIFETIME` — **le code du serveur, sans traduction
 *   intermédiaire**. Une seule source, et rien de sensible dans l'URL (la règle
 *   du `#552` tient : un code de raison, pas une donnée).*
 */
export const RAISON_CLE: Record<string, CleI18n> = {
  REFRESH_TOKEN_REUSED: 'auth.sessionJetonRejoue',
  SESSION_MAX_LIFETIME: 'auth.sessionDureeMax',
  SESSION_EXPIRED:      'auth.sessionInactivite',
};

/**
 * ⚠⚠ **POURQUOI `SESSION_EXPIRED` NE DIT PAS LA MÊME CHOSE ICI ET LÀ.**
 *   *Dans une réponse d'API, il annonce **un refus en cours** — « Session
 *   invalide ou expirée ». Sur `/login`, il explique **une fermeture déjà
 *   faite** — « Votre session s'est fermée après 30 minutes sans activité. Ce
 *   n'est pas une panne. » Même fait, deux moments, deux phrases. **Ce n'est
 *   pas un doublon : c'est la distinction que le `#552` a gravée** — dire
 *   pourquoi, plutôt que de laisser croire à une panne.*
 */
export function estRaisonDeconnexion(v: string | null): v is keyof typeof RAISON_CLE {
  return v !== null && v in RAISON_CLE;
}
